import { supabase } from "@/integrations/supabase/client";
import { ensureIdentity, getSessionId, getVisitorId } from "@/lib/visitorId";
import { gaEvent } from "@/lib/gtag";

let signedInIdentifier: string | null = null;

// Mint visitor/session ids at module load, before any caller can fire an event.
ensureIdentity();

/** Set/clear the signed-in user's identifier used as the preferred event identity. */
export function setTrackedUser(identifier: string | null): void {
  signedInIdentifier = identifier;
}

/** Suppress identical events fired repeatedly in a short window (render loops, polling). */
const DEDUPE_MS = 5000;
const recent = new Map<string, number>();

function isDuplicate(key: string): boolean {
  const now = Date.now();
  for (const [k, t] of recent) if (now - t > DEDUPE_MS) recent.delete(k);
  const last = recent.get(key);
  recent.set(key, now);
  return last !== undefined && now - last < DEDUPE_MS;
}

/**
 * Fire-and-forget event forwarding to the internal Command Centre ops dashboard,
 * mirrored into Google Analytics. Never throws and never blocks the caller.
 *
 * Only real, meaningful product actions belong here. High-frequency plumbing
 * (widget/iframe chatter, polling ticks, progress-status updates, intermediate
 * scroll milestones) must use `trackDebug` instead so the activity stream the
 * dashboard reads stays readable.
 */
export function trackEvent(
  eventType: string,
  eventData?: Record<string, unknown>,
  userIdentifier?: string
): void {
  try {
    const visitorId = getVisitorId();
    const sessionId = getSessionId();
    const payload = {
      ...(eventData ?? {}),
      visitor_id: visitorId,
      session_id: sessionId,
    };
    const identifier = userIdentifier || signedInIdentifier || visitorId;

    const path =
      typeof window !== "undefined"
        ? window.location.pathname + window.location.search
        : "";

    if (isDuplicate(`${eventType}|${path}|${JSON.stringify(eventData ?? {})}`)) {
      trackDebug(`duplicate:${eventType}`, eventData);
      return;
    }

    gaEvent(eventType, payload);

    void supabase.functions
      .invoke("log-command-centre-event", {
        body: {
          eventType,
          eventData: payload,
          userIdentifier: identifier,
          pagePath:
            typeof window !== "undefined" ? window.location.pathname : undefined,
        },
      })
      .catch((err) => console.error("trackEvent failed:", err));
  } catch (err) {
    console.error("trackEvent failed:", err);
  }
}

/**
 * Low-priority technical/diagnostic channel. Console only — never reaches the
 * Command Centre event stream or GA. Use for raw plumbing signals.
 */
export function trackDebug(
  eventType: string,
  eventData?: Record<string, unknown>
): void {
  try {
    if (typeof window === "undefined") return;
    console.debug("[track:debug]", eventType, {
      ...(eventData ?? {}),
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
    });
  } catch {
    /* never throw from tracking */
  }
}
